import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, RefreshCw, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLockStore } from '@/app/store/lock'
import { useTickerStore } from '@/app/store/ticker'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { useCan } from '@/hooks/useCan'
import { tickerApi, type NotificationRow } from '../api'
import { useNewMessageCount, useNotificationDetail, useTickerRecoverySets } from '../hooks'
import { compactAgo, searchWords } from '../format'
import { buildFields, str, type Field } from '../fields'
import { HIGHLIGHT_ROW_CLASS, TONE_CLASS } from '../palette'
import { Highlight } from './Highlight'
import { KindAvatar, KindChip, SampleChip } from './KindChip'
import { SafeRichText } from './SafeRichText'

/**
 * "All messages": the ticker's history, as the prototype's side panel.
 *
 * - A FROZEN snapshot. Nothing in the list moves while it is open; the header counts what has
 *   arrived since (GET /count?after=<top id>) and Refresh takes a new snapshot.
 * - Search is server-side (all words must match), debounced, with the words marked in the rows.
 * - Rows are an aligned grid (chip, avatar, text, time) whose text wraps. A row expands to the full
 *   record from GET /api/notifications/:id. A banner card that was clicked opens the panel on its
 *   own row, expanded and highlighted.
 * - "Load older" pages back with before=<oldest id>.
 * - A recovery-set filter (Andrew, 29 Sep 2026): by default every set the user receives; choosing
 *   one narrows the list, the search and the "N new" count to that set. Choosing takes a new
 *   snapshot, as a new search does.
 *
 * The hand-rolled right-hand panel follows OnlineUsersPanel (there is no sheet component), with a
 * scrim as in the prototype. It is a modal dialog in behaviour as well as in name: opening it moves
 * the focus to its search box, Tab and Shift+Tab stay inside it, and closing it gives the focus
 * back to whatever opened it (or to the ticker's icon, when that has gone, as a conveyor card has).
 *
 * It is modal over the APP, not over every layer. The lock screen and the app's other dialogs
 * (SessionTimeoutDialog, any Radix dialog) are rendered as siblings above it while it stays
 * mounted, so the trap leaves alone any focus that lands inside another dialog, does nothing while
 * the screen is locked, and on closing does not pull the focus back out of one. Locking also closes
 * the panel (the ticker store).
 */
export function NotificationsPanel() {
    const panel = useTickerStore((s) => s.panel)
    if (!panel.open) return null
    // Keyed on the focus, so a second card click while open starts a fresh snapshot on that row.
    return <PanelBody key={panel.focusId ?? 'all'} focusId={panel.focusId} />
}

const PAGE = 50
const SEARCH_DEBOUNCE_MS = 300
/** A clicked card is recent; how far back to page looking for its row before giving up. */
const FOCUS_MAX_PAGES = 5

/** Another layer that holds its own focus: a dialog other than this panel. */
function inOtherLayer(target: EventTarget | null, panel: HTMLElement | null): boolean {
    if (!(target instanceof Element)) return false
    const layer = target.closest('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')
    return layer != null && layer !== panel
}

interface Snapshot {
    rows: NotificationRow[]
    hasMore: boolean
    /** Search results only: matches across all pages. */
    total: number | null
}

function PanelBody({ focusId }: { focusId: number | null }) {
    const { t, i18n } = useTranslation()
    const closePanel = useTickerStore((s) => s.closePanel)
    const setIds = useTickerStore((s) => s.prefs?.recoverySetIds)
    const mySets = useMemo(() => setIds ?? [], [setIds])
    // The sets, named and with whether they have messages; usually already read by the ticker menu.
    const setList = useTickerRecoverySets(true)
    /** The one set the list is narrowed to; null is every set the user receives. */
    const [onlySet, setOnlySet] = useState<number | null>(null)
    // The filter's choices. All messages is history, so it offers every set that has messages -
    // a closed set's included, or its messages could never be found again - plus the sets received
    // (Andrew, 29 Sep 2026). Newest first, as the ticker menu lists them; a closed set says so.
    const filterChoices = useMemo(() => {
        const known = setList.data ?? []
        const listed = new Set(known.map((set) => set.id))
        return [
            ...known.filter((set) => set.hasMessages || mySets.includes(set.id)),
            ...mySets.filter((id) => !listed.has(id)).map((id) => ({ id, description: `#${id}`, open: true })),
        ].map((set) => ({ id: set.id, name: set.open ? set.description : t('ticker.panel.closedSet', { name: set.description }) }))
    }, [mySets, setList.data, t])
    // A choice that is no longer offered narrows nothing.
    const narrowed = onlySet != null && filterChoices.some((choice) => choice.id === onlySet) ? onlySet : null
    const sets = useMemo(() => (narrowed != null ? [narrowed] : mySets), [narrowed, mySets])

    const [input, setInput] = useState('')
    const [query, setQuery] = useState('')
    const [snapshot, setSnapshot] = useState<Snapshot>({ rows: [], hasMore: false, total: null })
    /** The newest id when the snapshot was taken; the "N new" count is everything above it. */
    const [topId, setTopId] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [loadingOlder, setLoadingOlder] = useState(false)
    const [failed, setFailed] = useState(false)
    const [expandedId, setExpandedId] = useState<number | null>(focusId)
    const [highlightId, setHighlightId] = useState<number | null>(focusId)
    const [spinning, setSpinning] = useState(false)
    const listRef = useRef<HTMLDivElement>(null)
    const asideRef = useRef<HTMLElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const request = useRef(0)
    const words = useMemo(() => searchWords(query), [query])

    // Debounce the search box into the query the server is asked.
    useEffect(() => {
        const timer = setTimeout(() => setQuery(input.trim()), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [input])

    const loadFirst = useCallback(
        async (opts: { findFocus?: number | null } = {}) => {
            const seq = ++request.current
            setLoading(true)
            setFailed(false)
            try {
                if (!sets.length) {
                    setSnapshot({ rows: [], hasMore: false, total: null })
                    setTopId(0)
                    return
                }
                if (query) {
                    const [page, newest] = await Promise.all([
                        tickerApi.search(sets, query, { limit: PAGE }),
                        tickerApi.history(sets, { limit: 1 }),
                    ])
                    if (seq !== request.current) return
                    setSnapshot({ rows: page.items, hasMore: page.hasMore, total: page.total })
                    setTopId(newest.items[0]?.id ?? 0)
                    return
                }
                let page = await tickerApi.history(sets, { limit: PAGE })
                let rows = page.items
                const top = rows[0]?.id ?? 0
                // A clicked card opens on its own row: page back until it is in the snapshot.
                for (let n = 0; opts.findFocus != null && page.hasMore && n < FOCUS_MAX_PAGES; n++) {
                    if (rows.some((r) => r.id === opts.findFocus)) break
                    page = await tickerApi.history(sets, { before: rows[rows.length - 1].id, limit: PAGE })
                    rows = rows.concat(page.items)
                }
                if (seq !== request.current) return
                setSnapshot({ rows, hasMore: page.hasMore, total: null })
                setTopId(top)
            } catch {
                if (seq === request.current) setFailed(true)
            } finally {
                if (seq === request.current) setLoading(false)
            }
        },
        [sets, query],
    )

    // The first snapshot, and a new one whenever the search or the set filter changes.
    // Deliberately NOT on new messages: the list is frozen until Refresh.
    const firstLoad = useRef(true)
    useEffect(() => {
        const findFocus = firstLoad.current ? focusId : null
        firstLoad.current = false
        void loadFirst({ findFocus })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, narrowed])

    // Stored messages were deleted from this browser (Clear sample messages): the frozen snapshot
    // may hold rows that no longer exist, so take a new one. Only on a change, not on mount.
    const historyEpoch = useTickerStore((s) => s.historyEpoch)
    const epochSeen = useRef(historyEpoch)
    useEffect(() => {
        if (historyEpoch === epochSeen.current) return
        epochSeen.current = historyEpoch
        setExpandedId(null)
        setHighlightId(null)
        void loadFirst()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyEpoch])

    // Bring the clicked card's row into view once it is on screen.
    useEffect(() => {
        if (loading || highlightId == null) return
        const row = listRef.current?.querySelector(`[data-row-id="${highlightId}"]`)
        row?.scrollIntoView?.({ block: 'center' })
    }, [loading, highlightId])

    const newCount = useNewMessageCount(sets, topId ?? 0, !loading && topId != null)
    const waiting = newCount.data ?? 0

    const refresh = () => {
        setSpinning(true)
        setTimeout(() => setSpinning(false), 500)
        setHighlightId(null)
        void loadFirst()
        listRef.current?.scrollTo?.({ top: 0 })
    }

    const loadOlder = async () => {
        const oldest = snapshot.rows[snapshot.rows.length - 1]
        if (!oldest) return
        const seq = request.current
        setLoadingOlder(true)
        try {
            const page = query
                ? await tickerApi.search(sets, query, { before: oldest.id, limit: PAGE })
                : await tickerApi.history(sets, { before: oldest.id, limit: PAGE })
            if (seq !== request.current) return
            setSnapshot((s) => ({
                rows: s.rows.concat(page.items.filter((r) => !s.rows.some((x) => x.id === r.id))),
                hasMore: page.hasMore,
                total: 'total' in page ? (page as { total: number }).total : s.total,
            }))
        } catch {
            if (seq === request.current) setFailed(true)
        } finally {
            setLoadingOlder(false)
        }
    }

    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => {
            // Escape in a layer above (the lock screen, a dialog) belongs to that layer.
            if (e.key !== 'Escape' || useLockStore.getState().locked || inOtherLayer(e.target, asideRef.current)) return
            closePanel()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [closePanel])

    // Focus in on open, kept in while open, given back on close.
    useEffect(() => {
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const aside = asideRef.current
        searchRef.current?.focus()
        let redirecting = false
        const onFocusIn = (e: FocusEvent) => {
            if (redirecting || !aside || !(e.target instanceof Node) || aside.contains(e.target)) return
            // Only the app beneath is trapped: never a layer above it, and nothing while locked.
            if (useLockStore.getState().locked || inOtherLayer(e.target, aside)) return
            redirecting = true
            searchRef.current?.focus()
            redirecting = false
        }
        document.addEventListener('focusin', onFocusIn)
        return () => {
            document.removeEventListener('focusin', onFocusIn)
            // Give the focus back only if it was still the panel's: by now its element has left the
            // page (the body has the focus) or it is still inside it. Focus that has already moved
            // somewhere else - the lock screen's password box - stays where it is.
            const active = document.activeElement
            if (active && active !== document.body && !aside?.contains(active)) return
            const back =
                opener && opener !== document.body && opener.isConnected
                    ? opener
                    : document.querySelector<HTMLElement>('[data-ticker-trigger]')
            back?.focus()
        }
    }, [])

    const trapTab = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key !== 'Tab' || !asideRef.current) return
        const focusable = Array.from(
            asideRef.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
        )
        if (!focusable.length) return
        const first = focusable[0]
        const lastEl = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault()
            lastEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastEl) {
            e.preventDefault()
            first.focus()
        }
    }

    const lang = i18n.language
    const nowLabel = t('ticker.panel.now')
    const searching = query.length > 0

    return (
        <>
            <div
                aria-hidden
                data-testid="ticker-scrim"
                onClick={closePanel}
                className="fixed inset-0 z-[190] bg-[rgba(16,30,50,.14)] animate-in fade-in-0 duration-200 motion-reduce:animate-none dark:bg-black/40"
            />
            <aside
                ref={asideRef}
                role="dialog"
                aria-modal="true"
                aria-label={t('ticker.panel.title')}
                onKeyDown={trapTab}
                className="fixed inset-y-0 right-0 z-[200] flex w-[380px] max-w-[90vw] flex-col border-l-[0.5px] bg-background shadow-[-10px_0_30px_rgba(16,30,50,.12)] animate-in slide-in-from-right duration-300 motion-reduce:animate-none"
            >
                <div className="flex items-center justify-between border-b-[0.5px] px-[18px] pb-[13px] pt-[15px]">
                    <div>
                        <h2 className="m-0 text-[15px] font-bold">{t('ticker.panel.title')}</h2>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{t('ticker.panel.snapshot')}</div>
                    </div>
                    <button
                        type="button"
                        onClick={closePanel}
                        aria-label={t('ticker.panel.close')}
                        className="inline-flex h-[22px] w-[22px] items-center justify-center rounded text-muted-foreground hover:text-foreground"
                    >
                        <X className="h-[18px] w-[18px]" />
                    </button>
                </div>

                <div className="border-b-[0.5px] px-[18px] py-2.5">
                    <div className="flex h-[34px] items-center gap-2 rounded-lg border-[0.5px] border-input bg-background px-2.5 focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15">
                        <Search aria-hidden className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground" />
                        <input
                            ref={searchRef}
                            type="text"
                            role="searchbox"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t('ticker.panel.searchPlaceholder')}
                            aria-label={t('ticker.panel.searchPlaceholder')}
                            autoComplete="off"
                            className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-foreground outline-none"
                        />
                        {input && (
                            <button
                                type="button"
                                aria-label={t('ticker.panel.clearSearch')}
                                onClick={() => setInput('')}
                                className="inline-flex text-muted-foreground hover:text-foreground"
                            >
                                <X className="h-[15px] w-[15px]" />
                            </button>
                        )}
                    </div>
                    {searching && !loading && snapshot.total != null && (
                        <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="ticker-match-count">
                            {snapshot.total === 1
                                ? t('ticker.panel.matchCountOne')
                                : t('ticker.panel.matchCount', { count: snapshot.total })}
                            {/* Only some of the matches are loaded until "Load older" is pressed. */}
                            {snapshot.hasMore && ` · ${t('ticker.panel.matchShowing', { shown: snapshot.rows.length })}`}
                        </div>
                    )}
                    {/* Always shown, even with one set to choose, so it is there to be found (Andrew,
                        29 Sep 2026). A native select: a portalled listbox would sit outside the panel,
                        and the focus trap would pull the focus back out of it. */}
                    {(
                        <select
                            value={narrowed ?? ''}
                            onChange={(e) => setOnlySet(e.target.value ? Number(e.target.value) : null)}
                            aria-label={t('ticker.panel.setFilter')}
                            data-testid="ticker-set-filter"
                            className="mt-2 h-[30px] w-full min-w-0 truncate rounded-lg border-[0.5px] border-input bg-background px-2 text-[12px] text-foreground outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15"
                        >
                            <option value="">{t('ticker.panel.allMySets')}</option>
                            {filterChoices.map((choice) => (
                                <option key={choice.id} value={choice.id}>
                                    {choice.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2.5 border-b-[0.5px] bg-muted/40 px-[18px] py-2.5">
                    <span className="text-[11.5px] text-muted-foreground" data-testid="ticker-new-count" aria-live="polite">
                        {waiting > 0 ? (
                            <span className="font-semibold text-primary">
                                {waiting === 1 ? t('ticker.panel.newWaitingOne') : t('ticker.panel.newWaiting', { count: waiting })}
                            </span>
                        ) : (
                            t('ticker.panel.upToDate')
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={refresh}
                        className={cn(
                            'inline-flex items-center gap-[7px] rounded-lg border-[0.5px] bg-background px-3 py-[7px] text-[12px] font-semibold text-muted-foreground',
                            waiting > 0 && 'border-primary bg-primary/5 text-primary',
                        )}
                    >
                        <RefreshCw
                            aria-hidden
                            className={cn('h-3.5 w-3.5 transition-transform duration-500 motion-reduce:transition-none', spinning && 'rotate-[360deg]')}
                        />
                        {t('ticker.panel.refresh')}
                    </button>
                </div>

                <div ref={listRef} className="flex-1 overflow-y-auto px-2.5 pb-[18px] pt-1.5" role="list" aria-busy={loading}>
                    {loading ? (
                        <Empty>{t('ticker.loading')}</Empty>
                    ) : failed && snapshot.rows.length === 0 ? (
                        <Empty>
                            {t('ticker.panel.loadFailed')}{' '}
                            <button type="button" className="font-semibold text-primary underline" onClick={() => void loadFirst()}>
                                {t('ticker.panel.retry')}
                            </button>
                        </Empty>
                    ) : !sets.length ? (
                        <Empty>{t('ticker.panel.noSets')}</Empty>
                    ) : snapshot.rows.length === 0 ? (
                        <Empty>{searching ? t('ticker.panel.noMatches') : t('ticker.panel.empty')}</Empty>
                    ) : (
                        <>
                            {snapshot.rows.map((row) => (
                                <PanelRow
                                    key={row.id}
                                    row={row}
                                    words={words}
                                    expanded={expandedId === row.id}
                                    highlighted={highlightId === row.id}
                                    ago={compactAgo(row.created_at, lang, nowLabel)}
                                    onToggle={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                                />
                            ))}
                            {snapshot.hasMore && (
                                <div className="flex justify-center pt-3">
                                    <button
                                        type="button"
                                        onClick={() => void loadOlder()}
                                        disabled={loadingOlder}
                                        className="rounded-lg border-[0.5px] bg-background px-3 py-[7px] text-[12px] font-semibold text-primary hover:bg-primary/5 disabled:opacity-60"
                                    >
                                        {loadingOlder ? t('ticker.loading') : t('ticker.panel.loadOlder')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </aside>
        </>
    )
}

function Empty({ children }: { children: ReactNode }) {
    return <div className="p-[30px] text-center text-[13px] text-muted-foreground">{children}</div>
}

function PanelRow({
    row,
    words,
    expanded,
    highlighted,
    ago,
    onToggle,
}: {
    row: NotificationRow
    words: readonly string[]
    expanded: boolean
    highlighted: boolean
    ago: string
    onToggle: () => void
}) {
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
        }
    }
    return (
        <div
            role="listitem"
            data-row-id={row.id}
            data-highlighted={highlighted ? 'true' : undefined}
            className={cn(
                'rounded-[9px] border-[0.5px] border-transparent border-b-border/60 px-2 py-[11px]',
                expanded && !highlighted && 'border-primary/20 bg-primary/[.04]',
                highlighted && HIGHLIGHT_ROW_CLASS,
            )}
        >
            <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={onToggle}
                onKeyDown={onKeyDown}
                className="grid cursor-pointer grid-cols-[88px_28px_minmax(0,1fr)_30px] items-start gap-x-[9px] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <KindChip kind={row.kind} size="row" className="mt-px" />
                <KindAvatar kind={row.kind} initials={row.author?.initials ?? null} size="row" />
                <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold leading-[1.35] [overflow-wrap:anywhere]">
                        <Highlight text={row.title} words={words} />
                        {row.sample && <SampleChip size="row" className="ml-1.5 align-[1px]" />}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-[1.35] text-muted-foreground [overflow-wrap:anywhere]">
                        <Highlight text={row.summary} words={words} />
                    </div>
                </div>
                <span className="mt-0.5 flex flex-col items-end gap-[5px] whitespace-nowrap text-right text-[10.5px] text-muted-foreground" title={row.created_at}>
                    {ago}
                    <ChevronDown aria-hidden className={cn('h-[13px] w-[13px] transition-transform duration-200', expanded && 'rotate-180')} />
                </span>
            </div>
            {expanded && <RowDetail row={row} words={words} />}
        </div>
    )
}

function RowDetail({ row, words }: { row: NotificationRow; words: readonly string[] }) {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const closePanel = useTickerStore((s) => s.closePanel)
    const { formatDateTime } = useDateTimeFormat()
    const detail = useNotificationDetail(row.id)
    // The ticker is for anyone with ticker.view, and not all of them can open the pages a message
    // links to: each link is offered only with the permission its page checks (router.tsx).
    const canOpenObject = useCan('objects.read')
    const canOpenRecovery = useCan('recover-requests.read')

    const when = (v: unknown) => {
        const s = str(v)
        return s ? formatDateTime(s, { includeSeconds: true, hour12: false }) : null
    }

    const go = (to: string, state?: unknown) => {
        closePanel()
        navigate(to, state ? { state } : undefined)
    }

    const content = (() => {
        if (detail.isLoading) return <p className="text-[11.5px] text-muted-foreground">{t('ticker.loading')}</p>
        if (detail.isError || !detail.data) return <p className="text-[11.5px] text-destructive">{t('ticker.panel.detailFailed')}</p>
        const d = detail.data
        const fields = buildFields(d, t, i18n.language, when)
        const isRich = d.kind === 'engineer_update' || d.kind === 'management_update'
        const bodyLabel = isRich
            ? t('ticker.fields.update')
            : d.kind === 'task_issue'
              ? t('ticker.fields.issueDescription')
              : t('ticker.fields.notes')
        const objectId = d.object_id ?? str(d.details.objectId) ?? str(d.details.mainObjectId)
        const showSet = d.kind === 'management_update' || d.kind === 'rta_change'
        return (
            <>
                <div className="grid grid-cols-[118px_minmax(0,1fr)] gap-x-2.5 gap-y-[7px] text-[11.5px]">
                    {fields.map((f) => (
                        <FieldRow key={f.label} field={f} words={words} />
                    ))}
                </div>
                {d.body && (
                    <div className="mt-2.5 border-t-[0.5px] pt-2.5 text-[12px] leading-[1.5] text-foreground">
                        <span className="mb-[3px] block text-[11px] text-muted-foreground">{bodyLabel}</span>
                        {isRich ? (
                            <SafeRichText html={d.body} words={words} className="[overflow-wrap:anywhere] [&_a]:text-primary [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5" />
                        ) : (
                            <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
                                <Highlight text={d.body} words={words} />
                            </p>
                        )}
                    </div>
                )}
                <div className="mt-[11px] flex flex-wrap gap-2">
                    {canOpenObject && objectId && !showSet && (
                        <DetailAction onClick={() => go(`/objects/${encodeURIComponent(objectId)}`)}>{t('ticker.panel.viewObject')}</DetailAction>
                    )}
                    {canOpenRecovery && d.request_id && (
                        <DetailAction
                            onClick={() =>
                                go(`/recovery-runbook/${encodeURIComponent(d.request_id as string)}`, {
                                    objectID: objectId ?? undefined,
                                    objectDescription: str(d.details.objectDescription),
                                    from: location.pathname,
                                    readOnly: true,
                                })
                            }
                        >
                            {t('ticker.panel.viewRunbook')}
                        </DetailAction>
                    )}
                    {canOpenRecovery && showSet && (
                        <DetailAction onClick={() => go(`/recovery-management/sets/${d.recovery_set_id}`)}>{t('ticker.panel.viewSet')}</DetailAction>
                    )}
                </div>
            </>
        )
    })()

    return (
        <div
            className="mt-2.5 cursor-default rounded-[9px] border-[0.5px] bg-background px-3 pb-2.5 pt-3"
            data-testid="ticker-row-detail"
            onClick={(e) => e.stopPropagation()}
        >
            {content}
        </div>
    )
}

function FieldRow({ field, words }: { field: Field; words: readonly string[] }) {
    return (
        <>
            <div className="text-muted-foreground">{field.label}</div>
            <div className={cn('font-semibold text-foreground [overflow-wrap:anywhere]', field.tone && TONE_CLASS[field.tone])}>
                <Highlight text={field.value} words={words} />
            </div>
        </>
    )
}

function DetailAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-[7px] border-[0.5px] bg-background px-[11px] py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/5"
        >
            {children}
        </button>
    )
}
